import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Select,
  Input,
  Upload,
  Button,
  Space,
  Alert,
  Table,
  Tag,
  message,
  Divider,
  Modal,
  Radio,
} from 'antd';
import { UploadOutlined, CheckCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { formazioneAPI } from '../services/api.ts';
import { documentTypeLabel } from '../utils/documentTypes.ts';

const { Title, Text, Paragraph } = Typography;

interface DocOption {
  id: number;
  document_type: string;
  document_number: string;
  document_date: string | null;
  shipment_id: number | null;
  vehicle_plate: string | null;
}

interface ProductReport {
  customs_document_item_id: number;
  product_id: number;
  composite_code: string | null;
  original_quantity_loaded: number;
  excel_rows: number;
  excel_quantity_sum: number;
  errors: string[];
  conflicts: { field: string; current_value: string; excel_value: string }[];
}

interface ValidateResult {
  valid: boolean;
  has_conflicts: boolean;
  global_errors: string[];
  products: ProductReport[];
}

const FormazioneTmp: React.FC = () => {
  const [documents, setDocuments] = useState<DocOption[]>([]);
  const [documentId, setDocumentId] = useState<number | undefined>(undefined);
  const [targa, setTarga] = useState<string>('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<ValidateResult | null>(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const data = await formazioneAPI.listDocuments();
      setDocuments(data);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel caricamento dei documenti');
    }
  };

  const onSelectDocument = (id: number) => {
    setDocumentId(id);
    setResult(null);
    // Pre-compila la targa dalla spedizione collegata (modificabile). Per i documenti
    // senza spedizione (bollette manuali) il campo fa solo da filtro sulla colonna CAMION
    // del file: si propone il numero della bolletta.
    const doc = documents.find((d) => d.id === id);
    if (doc?.vehicle_plate) {
      setTarga(doc.vehicle_plate);
    } else if (doc && !doc.shipment_id) {
      setTarga(doc.document_number);
    }
  };

  const selectedDoc = documents.find((d) => d.id === documentId);
  const isDocWithoutShipment = !!selectedDoc && !selectedDoc.shipment_id;

  const resetResult = () => setResult(null);

  const canVerify = !!documentId && !!targa.trim() && !!file;

  const handleVerify = async () => {
    if (!documentId || !file) return;
    setValidating(true);
    setResult(null);
    try {
      const data: ValidateResult = await formazioneAPI.validate(file, documentId, targa.trim());
      setResult(data);
      if (data.valid) {
        message.success(
          data.has_conflicts
            ? 'Verifica completata: dati validi ma con conflitti di sovrascrittura.'
            : 'Verifica completata: tutti i dati sono validi.'
        );
      } else {
        message.warning('Verifica completata: sono presenti errori da correggere.');
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore durante la verifica');
    } finally {
      setValidating(false);
    }
  };

  const runExecute = async (conflictMode: string) => {
    if (!documentId || !file) return;
    setExecuting(true);
    try {
      const data = await formazioneAPI.execute(file, documentId, targa.trim(), conflictMode);
      if (data.status === 'ok') {
        message.success(data.message);
        setResult(null);
        setFile(null);
        setFileList([]);
      } else if (data.status === 'conflicts') {
        message.warning(data.message || 'Conflitti rilevati.');
      } else {
        message.error('Esecuzione non riuscita: dati non validi.');
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore durante la formazione');
    } finally {
      setExecuting(false);
    }
  };

  const handleExecute = () => {
    if (!result?.valid) return;

    if (result.has_conflicts) {
      let mode = 'overwrite';
      Modal.confirm({
        title: 'Conflitti di sovrascrittura rilevati',
        width: 520,
        content: (
          <div>
            <Paragraph>
              Alcuni campi risultano già valorizzati e diversi dai valori del file Excel.
              Scegli come procedere:
            </Paragraph>
            <Radio.Group defaultValue="overwrite" onChange={(e) => (mode = e.target.value)}>
              <Space direction="vertical">
                <Radio value="overwrite">Sovrascrivi i valori esistenti con quelli dell'Excel</Radio>
                <Radio value="skip">Salta i prodotti in conflitto (forma solo gli altri)</Radio>
              </Space>
            </Radio.Group>
          </div>
        ),
        okText: 'Procedi',
        cancelText: 'Annulla tutto',
        onOk: () => runExecute(mode),
      });
    } else {
      runExecute('abort');
    }
  };

  const productColumns = [
    { title: 'Prodotto (ID)', dataIndex: 'product_id', key: 'product_id', width: 110 },
    { title: 'Codice composto', dataIndex: 'composite_code', key: 'composite_code' },
    { title: 'Qtà documento', dataIndex: 'original_quantity_loaded', key: 'oql', width: 120 },
    { title: 'Righe Excel', dataIndex: 'excel_rows', key: 'excel_rows', width: 100 },
    { title: 'Somma Excel', dataIndex: 'excel_quantity_sum', key: 'sum', width: 110 },
    {
      title: 'Esito',
      key: 'status',
      render: (_: any, record: ProductReport) => {
        if (record.errors.length > 0) {
          return (
            <Space direction="vertical" size={0}>
              {record.errors.map((e, i) => (
                <Tag color="red" key={i} style={{ whiteSpace: 'normal' }}>{e}</Tag>
              ))}
            </Space>
          );
        }
        if (record.conflicts.length > 0) {
          return (
            <Space direction="vertical" size={0}>
              <Tag color="orange">Valido con conflitti</Tag>
              {record.conflicts.map((c, i) => (
                <Text key={i} type="warning" style={{ fontSize: 12 }}>
                  {c.field}: "{c.current_value}" → "{c.excel_value}"
                </Text>
              ))}
            </Space>
          );
        }
        return <Tag color="green">OK</Tag>;
      },
    },
  ];

  return (
    <div>
      <Title level={3}>Formazione-Tmp</Title>
      <Paragraph type="secondary">
        Suddivide i prodotti di un documento doganale H2/H1 (IM7/IM4) <strong>già elaborato</strong> in
        pallet/colli secondo un file Excel di formazione, preservando le prenotazioni. Funzione
        temporanea riservata all'amministratore.
      </Paragraph>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>Documento (H2/H1 elaborati)</Text>
            <Select
              showSearch
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Seleziona un documento"
              value={documentId}
              onChange={onSelectDocument}
              optionFilterProp="label"
              options={documents.map((d) => ({
                value: d.id,
                label: `${documentTypeLabel(d.document_type)} ${d.document_number}${d.document_date ? ' - ' + d.document_date : ''}${d.vehicle_plate ? ' - Targa: ' + d.vehicle_plate : (!d.shipment_id ? ' - senza spedizione' : '')}`,
              }))}
            />
          </div>

          <div>
            <Text strong>{isDocWithoutShipment ? 'Riferimento in colonna CAMION' : 'Targa / N° Container'}</Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder={isDocWithoutShipment ? 'Es. numero bolletta' : 'Es. TCKU6838331'}
              value={targa}
              onChange={(e) => { setTarga(e.target.value); resetResult(); }}
            />
            {isDocWithoutShipment && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Documento senza spedizione: nessuna targa da verificare. Vengono lette solo le righe del file
                la cui colonna CAMION contiene questo valore (di norma il numero della bolletta).
              </Text>
            )}
          </div>

          <div>
            <Text strong>File Excel di formazione</Text>
            <div style={{ marginTop: 4 }}>
              <Upload
                accept=".xls,.xlsx"
                maxCount={1}
                fileList={fileList}
                beforeUpload={(f) => {
                  setFile(f as unknown as File);
                  setFileList([{ uid: '-1', name: f.name, status: 'done' } as UploadFile]);
                  resetResult();
                  return false;
                }}
                onRemove={() => {
                  setFile(null);
                  setFileList([]);
                  resetResult();
                }}
              >
                <Button icon={<UploadOutlined />}>Seleziona file (.xls / .xlsx)</Button>
              </Upload>
            </div>
          </div>

          <Space>
            <Button
              type="default"
              icon={<CheckCircleOutlined />}
              onClick={handleVerify}
              loading={validating}
              disabled={!canVerify}
            >
              Verifica
            </Button>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleExecute}
              loading={executing}
              disabled={!result?.valid}
            >
              Esegui Formazione
            </Button>
          </Space>
        </Space>
      </Card>

      {result && (
        <Card style={{ marginTop: 16 }}>
          {result.global_errors.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Errori generali"
              description={
                <ul style={{ marginBottom: 0 }}>
                  {result.global_errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              }
            />
          )}

          {result.global_errors.length === 0 && (
            <>
              <Alert
                type={result.valid ? (result.has_conflicts ? 'warning' : 'success') : 'error'}
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  result.valid
                    ? (result.has_conflicts
                        ? 'Dati validi, ma con conflitti di sovrascrittura.'
                        : 'Tutti i dati sono validi. Puoi eseguire la formazione.')
                    : 'Sono presenti errori: correggi il file o il documento prima di procedere.'
                }
              />
              <Divider orientation="left">Dettaglio per prodotto</Divider>
              <Table
                rowKey="customs_document_item_id"
                columns={productColumns}
                dataSource={result.products}
                pagination={false}
                size="small"
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default FormazioneTmp;
